import { useCallback, useEffect, useState } from 'react';
import { useAtom } from 'jotai';

import { useWorkerJobPositionsService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';
import { useVariableOptions } from '@restworld/utils-common';
import { JobApplication } from '@restworld/utility-types';

import {
  setApplyJobStatusDataAtom,
  setListWorkerJobApplicationsHistoryDataAtom,
  setApplicationStateAtom,
  setOpenKeysAtom,
  setCompletedKeysAtom,
  setActiveKeyAtom,
  getApplyJobStatusDataAtom,
  // getListWorkerJobApplicationsHistoryDataAtom,
  accomplishedStepsBeforeDiscardedAtom,
  accomplishedStepsAtom,
  getWorkerDiscardedReasonVariableAtom,
  setWorkerDiscardedReasonVariableAtom,
  setIsSpontaneousApplicationAtom,
  getDiscardDataAtom,
  getSelectedDiscardedReasonAtom,
  getDiscardedOtherReasonAtom,
  setApplicationDiscardSuccessAtom,
  setIsApplicationDiscardedAtom,
  setApplicationDiscardLoadingAtom,
  getIsSpontaneousApplicationAtom,
} from './application-details.context';
import {
  TimeLineKeysType,
  timelineIdList,
  timelineIdMapping,
  pendingStatusKey,
  hiredStatusKeys,
  rejectedStatusKeys,
  notInterestedStatusKeys,
} from './application-details.constants';
import { usePreInterview } from './use-pre-interview';

const useApplyJobStatus = () => {
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const [, setApplyJobStatusData] = useAtom(setApplyJobStatusDataAtom);
  const [, setApplicationState] = useAtom(setApplicationStateAtom);
  const workerJobPositionsService = useWorkerJobPositionsService();
  const callApplyJobStatus = useCallback(
    async ({ id }: { id: string }) => {
      try {
        setLoading(true);
        const applyJobStatusData = await workerJobPositionsService.jobApplyStatus({
          worker_id: user?.id,
          id,
        });

        setApplyJobStatusData(applyJobStatusData.data);
        setLoading(false);
      } catch (error) {
        if (error.code === 404) setApplicationState('not-found');
        setLoading(false);
        console.error(error, 'Error on getting job apply status');
      }
    },
    [setApplicationState, setApplyJobStatusData, user?.id, workerJobPositionsService]
  );

  return { callApplyJobStatus, loading };
};
const useListWorkerJobApplicationsHistory = () => {
  const [loading, setLoading] = useState(false);
  const [, setListWorkerJobApplicationsHistoryData] = useAtom(setListWorkerJobApplicationsHistoryDataAtom);

  const workerJobPositionsService = useWorkerJobPositionsService();
  const callListWorkerJobApplicationsHistory = useCallback(
    async ({ id }: { id: string }) => {
      try {
        setLoading(true);
        const response = await workerJobPositionsService.listWorkerJobApplicationsHistory({
          id,
        });
        setListWorkerJobApplicationsHistoryData(response.data);
        setLoading(false);
      } catch (error) {
        console.error(error, 'Error on getting List Worker JobApplications History Data');
      }
    },
    [setListWorkerJobApplicationsHistoryData, workerJobPositionsService]
  );

  return {
    loading,
    callListWorkerJobApplicationsHistory,
  };
};
const useCurrentApplicationStatus = () => {
  const [applyJobStatusData] = useAtom(getApplyJobStatusDataAtom);
  // const [listWorkerJobApplicationsHistoryData] = useAtom(getListWorkerJobApplicationsHistoryDataAtom);
  const {
    discarded_from_status_key: discardedFromStatusKey,
    status_key: statusKey,
    restaurant_id,
  } = applyJobStatusData;
  const [, setOpenKeys] = useAtom(setOpenKeysAtom);
  const [, setCompletedKeys] = useAtom(setCompletedKeysAtom);
  const [, setActiveKey] = useAtom(setActiveKeyAtom);
  const [, setApplicationState] = useAtom(setApplicationStateAtom);
  const [accomplishedStepsBeforeDiscarded] = useAtom(accomplishedStepsBeforeDiscardedAtom);
  const [accomplishedSteps] = useAtom(accomplishedStepsAtom);
  const [, setIsSpontaneousApplication] = useAtom(setIsSpontaneousApplicationAtom);
  const preInterview = usePreInterview();

  const checkAndAssign = useCallback(() => {
    if (hiredStatusKeys.includes(statusKey)) {
      setApplicationState('accepted');
      setCompletedKeys([...timelineIdList]);
      setOpenKeys(['hired']);
      return;
    }
    if (rejectedStatusKeys.includes(statusKey)) {
      setCompletedKeys(accomplishedStepsBeforeDiscarded as TimeLineKeysType[]);
      setApplicationState('rejected');
      setOpenKeys(['rejected']);
      return;
    }
    if (notInterestedStatusKeys.includes(statusKey)) {
      setApplicationState('not-interested');
      setOpenKeys(['not-interested']);
      return;
    }
    if (pendingStatusKey.includes(statusKey)) {
      setApplicationState('pending');
      // The interview lives inside `worker_jp_interested`: the application keeps that status
      // for the whole time the chat is open, so the generic mapping below would make step 1
      // the current one and hand the interview a closed dot, putting the most urgent action
      // of the page behind a click. While it is pending it is the current step instead, and
      // step 1 moves to done, which it is: the application did arrive.
      if (preInterview?.status === 'pending') {
        setCompletedKeys(['application-submitted']);
        setOpenKeys(['interview']);
        setActiveKey('interview');
        return;
      }
      // Closed interview, completed or expired: the application was sent and the interview is
      // over, and neither fact comes back, so both steps are done. Nothing is the current step
      // after them, though. The application is still `worker_jp_interested`, so nothing has
      // reached the company yet, and marking "Candidatura inviata all'azienda!" as current
      // would announce something that has not happened; it turns current when the real status
      // says so. The interview stays open, because it is the last thing that happened and it
      // holds the link to reread the chat.
      if (preInterview) {
        setCompletedKeys(['application-submitted', 'interview']);
        setOpenKeys(['interview']);
        setActiveKey('');
        return;
      }
      setCompletedKeys(accomplishedSteps as TimeLineKeysType[]);
      setOpenKeys(
        Object.keys(timelineIdMapping).filter((key) => timelineIdMapping[key].includes(statusKey)) as TimeLineKeysType[]
      );
      setActiveKey(
        Object.keys(timelineIdMapping).filter((key) =>
          timelineIdMapping[key].includes(statusKey)
        )[0] as TimeLineKeysType
      );
    }
  }, [
    accomplishedSteps,
    accomplishedStepsBeforeDiscarded,
    preInterview,
    setActiveKey,
    setApplicationState,
    setCompletedKeys,
    setOpenKeys,
    statusKey,
  ]);

  useEffect(() => {
    if (!statusKey) {
      setCompletedKeys([]);
      setOpenKeys([]);
      setApplicationState('not-found');
      return;
    }
    if (restaurant_id) {
      setIsSpontaneousApplication(true);
      setCompletedKeys(['spontaneous-application']);
      if (rejectedStatusKeys.includes(statusKey)) {
        setApplicationState('rejected');
        setOpenKeys(['rejected']);
        return;
      }
      if (notInterestedStatusKeys.includes(statusKey)) {
        setApplicationState('not-interested');
        setOpenKeys(['not-interested']);
        return;
      }
      setOpenKeys(['spontaneous-application']);
      return;
    }
    checkAndAssign();
  }, [
    accomplishedSteps,
    accomplishedStepsBeforeDiscarded,
    checkAndAssign,
    discardedFromStatusKey,
    restaurant_id,
    setActiveKey,
    setApplicationState,
    setCompletedKeys,
    setIsSpontaneousApplication,
    setOpenKeys,
    statusKey,
  ]);
};
const useWorkerDiscardedReasonVariable = () => {
  const [workerDiscardedReasonVariable] = useAtom(getWorkerDiscardedReasonVariableAtom);
  const [, setWorkerDiscardedReasonVariable] = useAtom(setWorkerDiscardedReasonVariableAtom);

  const { fetchVariable, variableOptions } = useVariableOptions();

  useEffect(() => {
    if (!workerDiscardedReasonVariable?.length) fetchVariable(['worker_discarded_reason']);
  }, [workerDiscardedReasonVariable?.length, fetchVariable]);

  useEffect(() => {
    if (variableOptions?.['worker_discarded_reason']) {
      const workerDiscardedReasonVariableByWorker = variableOptions['worker_discarded_reason'].filter(
        (v) => v.parent_key === 'worker'
      );
      setWorkerDiscardedReasonVariable(workerDiscardedReasonVariableByWorker);
    }
  }, [variableOptions, setWorkerDiscardedReasonVariable]);
};
const useApplicationReinstate = ({ onSuccess }: { onSuccess?: () => void }) => {
  const [loading, setLoading] = useState(false);
  const workerJobPositionsService = useWorkerJobPositionsService();

  const [applyJobStatusData] = useAtom(getApplyJobStatusDataAtom);
  // const [, setCompletedKeys] = useAtom(setCompletedKeysAtom);
  const [, setOpenKeys] = useAtom(setOpenKeysAtom);
  const [, setActiveKey] = useAtom(setActiveKeyAtom);
  const [, setApplicationState] = useAtom(setApplicationStateAtom);
  const { user } = useAuth();

  const onApplicationReInstate = useCallback(async () => {
    try {
      setLoading(true);
      await workerJobPositionsService?.updateJobApplication({
        workerId: user?.id,
        params: {
          status_key: 'worker_jp_interested',
        },
        ...(applyJobStatusData.job_position_id ? { jobPositionId: applyJobStatusData.job_position_id } : {}),
        ...(applyJobStatusData.restaurant_id ? { restaurantId: applyJobStatusData.restaurant_id } : {}),
      });
      setOpenKeys(['application-submitted']);
      setActiveKey('application-submitted');
      setApplicationState('pending');
      if (onSuccess) onSuccess();
      setLoading(false);
    } catch (error) {
      console.error(error, 'Error while updating application status to worker_jp_interested');
      setLoading(false);
    }
  }, [
    applyJobStatusData.job_position_id,
    applyJobStatusData.restaurant_id,
    onSuccess,
    setActiveKey,
    setApplicationState,
    setOpenKeys,
    user?.id,
    workerJobPositionsService,
  ]);
  return {
    onApplicationReInstate,
    applicationReinstateLoading: loading,
  };
};

const useDiscardApplicationHandlers = () => {
  const { user } = useAuth();
  const [workerDiscardedReasonVariable] = useAtom(getWorkerDiscardedReasonVariableAtom);
  const [selectedDiscardedReason] = useAtom(getSelectedDiscardedReasonAtom);
  const [discardedOtherReason] = useAtom(getDiscardedOtherReasonAtom);
  const [discardData] = useAtom(getDiscardDataAtom);
  const [applyJobStatusData] = useAtom(getApplyJobStatusDataAtom);
  const [, setApplyJobStatusData] = useAtom(setApplyJobStatusDataAtom);
  const [, setApplicationDiscardSuccess] = useAtom(setApplicationDiscardSuccessAtom);
  const [, setIsApplicationDiscarded] = useAtom(setIsApplicationDiscardedAtom);
  const [, setApplicationDiscardLoading] = useAtom(setApplicationDiscardLoadingAtom);
  const [, setApplicationState] = useAtom(setApplicationStateAtom);
  const [, setOpenKeys] = useAtom(setOpenKeysAtom);
  const [, setCompletedKeys] = useAtom(setCompletedKeysAtom);
  const [isSpontaneousApplication] = useAtom(getIsSpontaneousApplicationAtom);
  const workerJobPositionsService = useWorkerJobPositionsService();
  // const { trackEvent } = useAnalytics();

  const updateDiscardApplication = useCallback(async () => {
    try {
      setApplicationDiscardLoading(true);
      const discardDataById = workerDiscardedReasonVariable.find((v) => v.key === selectedDiscardedReason);
      const payload = {
        workerId: user?.id,
        params: {
          discarded_by: discardData.discardedBy,
          status_key: discardData.discardedStatusKey,
          ...(selectedDiscardedReason !== 'other' ? { discarded_reason: discardDataById?.id } : {}),
          ...(discardedOtherReason && selectedDiscardedReason === 'other'
            ? { discarded_other_reason: discardedOtherReason }
            : {}),
          discarded_from_status_key: applyJobStatusData?.status_key,
        },
        ...(applyJobStatusData?.job_position_id ? { jobPositionId: applyJobStatusData.job_position_id } : {}),
        ...(applyJobStatusData?.restaurant_id ? { restaurantId: applyJobStatusData.restaurant_id } : {}),
      };
      await workerJobPositionsService.updateJobApplication(payload);
      // trackEvent('Application details', {
      //   phase: `A user discarded the application with the reason ${discardDataById?.label}`,
      //   type: 'user-action',
      // });
      setApplicationDiscardLoading(false);
      setApplicationDiscardSuccess(true);
      setIsApplicationDiscarded(true);
      setApplyJobStatusData({
        ...applyJobStatusData,
        discarded_by: 'worker',
        status_key: 'worker_jp_discarded',
      } as JobApplication);
      if (isSpontaneousApplication) setCompletedKeys(['spontaneous-application']);
      else setCompletedKeys(['application-submitted', 'application-submitted-to-the-company']);
      setApplicationState('rejected');
      setOpenKeys(['rejected']);
    } catch (error) {
      setApplicationDiscardLoading(false);
      console.error(error, 'Error while updating job application');
    }
  }, [
    applyJobStatusData,
    discardData.discardedBy,
    discardData.discardedStatusKey,
    discardedOtherReason,
    isSpontaneousApplication,
    selectedDiscardedReason,
    setApplicationDiscardLoading,
    setApplicationDiscardSuccess,
    setApplicationState,
    setApplyJobStatusData,
    setCompletedKeys,
    setIsApplicationDiscarded,
    setOpenKeys,
    // trackEvent,
    user?.id,
    workerDiscardedReasonVariable,
    workerJobPositionsService,
  ]);
  return {
    updateDiscardApplication,
  };
};

export {
  useApplyJobStatus,
  useListWorkerJobApplicationsHistory,
  useCurrentApplicationStatus,
  useWorkerDiscardedReasonVariable,
  useApplicationReinstate,
  useDiscardApplicationHandlers,
};
